/**
 * Message Repository Service
 *
 * Handles message fetching, caching, and pagination.
 * Manages communication with the backend API and sessionStorage cache.
 */

import type {
  GetMessagesResponseItem,
  SuccessGetMessagesResponse
} from "@9amhealth/openapi";
import { MessageControllerService } from "@9amhealth/openapi";
import { CHAT_PAGINATION, STORAGE_KEYS } from "../constants/ChatConstants";
import reportErrorSentry from "src/lib/reportErrorSentry";
import type { ChatBlocDependencies } from "../types/ChatBlocDependencies";

/**
 * MessageRepository manages message persistence and retrieval:
 * - Fetching messages from API (first page, newer, older)
 * - Caching messages to sessionStorage
 * - Parsing API messages to ChatMessage format
 * - Handling pagination state
 */
export class MessageRepository {
  /**
   * Tracks if more history is available for pagination
   */
  private moreHistoryAvailable = true;

  /**
   * Promise for ongoing message loading (prevents duplicate requests)
   */
  private loadingMessagesPromise?: Promise<SuccessGetMessagesResponse>;

  constructor(private deps: ChatBlocDependencies) {}

  /**
   * Load the first page of messages
   */
  async loadFirstPage(
    showLoading: boolean = true,
    onLoadingStart?: () => void,
    onLoadingEnd?: () => void
  ): Promise<GetMessagesResponseItem[]> {
    if (showLoading && onLoadingStart) {
      onLoadingStart();
    }

    try {
      const promise =
        this.loadingMessagesPromise ??
        MessageControllerService.listRecentMessages(
          CHAT_PAGINATION.MESSAGES_PER_PAGE
        );

      this.loadingMessagesPromise = promise;
      const { data } = await promise;
      this.loadingMessagesPromise = undefined;
      data.messages?.reverse();
      return data.messages ?? [];
    } catch (error) {
      reportErrorSentry(error);
      return [];
    } finally {
      if (showLoading && onLoadingEnd) {
        onLoadingEnd();
      }
    }
  }

  /**
   * Load newer messages (messages after the last known message)
   */
  async loadNewer(lastMessageId: string): Promise<GetMessagesResponseItem[]> {
    try {
      const { data } = await MessageControllerService.listNearbyMessages(
        lastMessageId,
        "newer",
        CHAT_PAGINATION.MESSAGES_PER_PAGE
      );
      data.messages?.reverse();
      return data.messages ?? [];
    } catch (error) {
      reportErrorSentry(error);
      return [];
    }
  }

  /**
   * Load older messages (messages before the first known message)
   */
  async loadOlder(firstMessageId: string): Promise<GetMessagesResponseItem[]> {
    try {
      const { data } = await MessageControllerService.listNearbyMessages(
        firstMessageId,
        "older",
        CHAT_PAGINATION.MESSAGES_PER_PAGE
      );

      const messages = data.messages ?? [];

      // Update pagination state
      if (
        messages.length === 0 ||
        messages.length < CHAT_PAGINATION.MESSAGES_PER_PAGE
      ) {
        this.moreHistoryAvailable = false;
      }

      return messages;
    } catch (error) {
      reportErrorSentry(error);
      return [];
    }
  }

  /**
   * Cache messages to sessionStorage
   * Sorts by timestamp and keeps only the last N messages
   */
  cacheMessages(messages: GetMessagesResponseItem[]): void {
    const sorted = messages.sort((a, b) => {
      const aDate = new Date(a.messageReceiveTimestamp);
      const bDate = new Date(b.messageReceiveTimestamp);
      return aDate.getTime() - bDate.getTime();
    });

    // Filter out local messages (don't cache them)
    const messagesToCache = sorted.filter((m) => !m.id.startsWith("local_"));

    this.deps.storage.setItem(
      STORAGE_KEYS.MESSAGES,
      JSON.stringify(
        messagesToCache.slice(CHAT_PAGINATION.MAX_CACHED_MESSAGES * -1)
      )
    );
  }

  /**
   * Load cached messages from sessionStorage
   */
  loadCached(): GetMessagesResponseItem[] {
    const cached = this.deps.storage.getItem(STORAGE_KEYS.MESSAGES);
    if (!cached) {
      return [];
    }

    const cachedMessages = JSON.parse(cached) as GetMessagesResponseItem[];
    return cachedMessages;
  }

  /**
   * Check if more history is available for pagination
   */
  hasMoreHistory(): boolean {
    return this.moreHistoryAvailable;
  }

  /**
   * Reset pagination state
   */
  resetPaginationState(): void {
    this.moreHistoryAvailable = true;
  }

  /**
   * Clear loading promise (used for cleanup)
   */
  clearLoadingPromise(): void {
    this.loadingMessagesPromise = undefined;
  }
}
